/**
 * Discount Display for Product Pages, Collection Pages & Floating Banner
 *
 * Works with ANY discount code automatically applied via /discount/CODE
 * referral links.
 *
 * How it works:
 *  1. Detects the discount code from the cart API (applied by /discount/CODE)
 *     — always uses the LAST code in the array (most recently applied)
 *  2. Looks up the discount percentage from the config rendered by
 *     snippets/discount-config.liquid (a JSON block in the page HTML).
 *     Falls back to auto-detection from cart items if possible.
 *  3. Shows discounted prices on product pages and collection pages
 *  4. Shows a floating banner on ALL pages (including homepage) so
 *     visitors from referral links know they have a deal active
 *
 * To add a new discount code, edit snippets/discount-config.liquid in
 * your Shopify theme code editor.
 */
(function () {
  'use strict';

  var CACHE_KEY = 'stinger_discount_info';

  function getProductSection() {
    return document.querySelector('[data-product-section]');
  }

  // =============================================
  // DISCOUNT CONFIG (from Liquid snippet)
  // =============================================

  /**
   * Read the discount config rendered by snippets/discount-config.liquid.
   * Returns an array of { code, percentage, label? } objects, or [].
   */
  function readDiscountConfig() {
    try {
      var el = document.getElementById('discount-config');
      if (!el) return [];
      var data = JSON.parse(el.textContent);
      if (Array.isArray(data)) return data;
    } catch (e) {
      console.warn('[DiscountDisplay] Could not parse discount-config JSON:', e);
    }
    return [];
  }

  /**
   * Look up a discount code in the Liquid config.
   * Returns { code, percentage, label? } or null.
   */
  function lookupInConfig(code) {
    var config = readDiscountConfig();
    var codeUpper = code.toUpperCase();
    for (var i = 0; i < config.length; i++) {
      if (config[i].code && config[i].code.toUpperCase() === codeUpper) {
        return config[i];
      }
    }
    return null;
  }

  // =============================================
  // DISCOUNT DETECTION
  // =============================================

  function getCachedDiscount(currentCode) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (data && data.code && ((typeof data.percentage === 'number' && data.percentage > 0) || (typeof data.fixed_amount === 'number' && data.fixed_amount > 0))) {
        if (currentCode && currentCode.toUpperCase() !== data.code.toUpperCase()) {
          console.log('[DiscountDisplay] Cached code "' + data.code + '" differs from current code "' + currentCode + '". Clearing cache.');
          sessionStorage.removeItem(CACHE_KEY);
          return null;
        }
        console.log('[DiscountDisplay] Using cached discount:', data.code, data.percentage + '%');
        return data;
      }
    } catch (e) { /* ignore */ }
    return null;
  }

  function cacheDiscount(info) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(info));
    } catch (e) { /* ignore */ }
  }

  async function fetchCart() {
    var resp = await fetch('/cart.js', { credentials: 'same-origin' });
    return resp.json();
  }

  /**
   * Get the discount code string from the cart (or URL param).
   * Uses the LAST code in the discount_codes array because Shopify
   * appends newly-applied codes — so the last one is the most recent.
   */
  async function getDiscountCode() {
    // 1. Check URL for ?discount=CODE parameter
    try {
      var params = new URLSearchParams(window.location.search);
      var urlCode = params.get('discount') || '';
      if (urlCode) return urlCode;
    } catch (e) { /* ignore */ }

    // 2. Check the cart API — use the LAST code (most recently applied)
    try {
      var cart = await fetchCart();
      console.log('[DiscountDisplay] Cart discount_codes:', JSON.stringify(cart.discount_codes));
      if (cart.discount_codes && cart.discount_codes.length > 0) {
        var lastCode = cart.discount_codes[cart.discount_codes.length - 1];
        return lastCode.code || '';
      }
    } catch (e) {
      console.warn('[DiscountDisplay] Failed to fetch cart:', e);
    }

    return '';
  }

  /**
   * Try to calculate the discount percentage from existing cart items.
   */
  function calcPercentageFromCart(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return 0;

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (item.discount_allocations && item.discount_allocations.length > 0) {
        var alloc = item.discount_allocations[0];
        var discountAmount = parseInt(alloc.amount, 10);
        var originalLinePrice = item.original_line_price || (item.original_price * item.quantity);
        if (originalLinePrice > 0 && discountAmount > 0) {
          var pct = Math.round((discountAmount / originalLinePrice) * 100);
          if (pct > 0 && pct <= 100) {
            console.log('[DiscountDisplay] Calculated ' + pct + '% from existing cart items.');
            return pct;
          }
        }
      }
    }

    if (cart.total_discount && cart.total_discount > 0) {
      var originalTotal = 0;
      for (var j = 0; j < cart.items.length; j++) {
        originalTotal += cart.items[j].original_line_price || (cart.items[j].original_price * cart.items[j].quantity);
      }
      if (originalTotal > 0) {
        var pct2 = Math.round((cart.total_discount / originalTotal) * 100);
        if (pct2 > 0 && pct2 <= 100) {
          console.log('[DiscountDisplay] Calculated ' + pct2 + '% from cart totals.');
          return pct2;
        }
      }
    }

    return 0;
  }

  /**
   * Master function: detect the active discount code AND its details.
   *
   * Returns an object with:
   *   code         (string)  — the coupon code
   *   percentage   (number)  — percent off, or 0 if fixed/unknown
   *   fixed_amount (number)  — cents off (e.g. 500 = $5), or 0 if percentage/unknown
   *   label        (string|null) — optional custom banner label
   *
   * Priority order:
   *  1. sessionStorage cache (if code matches)
   *  2. Liquid config (snippets/discount-config.liquid)
   *  3. Auto-detect from cart items (if any are in the cart)
   *  4. Show banner without prices as last resort
   */
  async function getActiveDiscount() {
    // 1. Get the current discount code
    var code = await getDiscountCode();
    if (!code) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }

    // 2. Check sessionStorage cache (matches current code?)
    var cached = getCachedDiscount(code);
    if (cached) return cached;

    console.log('[DiscountDisplay] Discount code found: "' + code + '". Looking up details...');

    var percentage = 0;
    var fixedAmount = 0;
    var label = null;

    // 3. Look up in the Liquid config
    var configEntry = lookupInConfig(code);
    if (configEntry) {
      label = configEntry.label || null;

      if (configEntry.percentage > 0) {
        percentage = configEntry.percentage;
        console.log('[DiscountDisplay] Found in config: ' + code + ' = ' + percentage + '% off');
      } else if (configEntry.fixed_amount > 0) {
        fixedAmount = configEntry.fixed_amount;
        console.log('[DiscountDisplay] Found in config: ' + code + ' = $' + (fixedAmount / 100).toFixed(2) + ' off');
      }
    }

    // 4. If not in config, try to calculate from existing cart items
    if (percentage <= 0 && fixedAmount <= 0) {
      console.log('[DiscountDisplay] Code "' + code + '" not in config. Trying cart items...');
      var cart = await fetchCart();
      percentage = calcPercentageFromCart(cart);
    }

    // 5. Build result
    if (percentage > 0 || fixedAmount > 0) {
      var result = {
        code: code,
        percentage: percentage,
        fixed_amount: fixedAmount,
        label: label
      };
      cacheDiscount(result);
      return result;
    }

    // 6. Last resort — show banner without prices
    console.warn('[DiscountDisplay] Could not determine discount for "' + code + '". Add it to snippets/discount-config.liquid. Showing banner without prices.');
    return { code: code, percentage: 0, fixed_amount: 0, label: null };
  }

  // =============================================
  // PRICE CALCULATION
  // =============================================

  /**
   * Calculate the discounted price in cents.
   *   - percentage: e.g. 20 = 20% off  → $50 becomes $40
   *   - fixed_amount: in cents, e.g. 500 = $5 off → $50 becomes $45
   * Returns the new price in cents (floored to 0 minimum).
   */
  function getDiscountedPrice(originalPrice, discount) {
    if (discount.percentage > 0) {
      return Math.round(originalPrice * (1 - discount.percentage / 100));
    }
    if (discount.fixed_amount > 0) {
      return Math.max(0, originalPrice - discount.fixed_amount);
    }
    return originalPrice;
  }

  /**
   * Returns the human-readable badge text for the discount.
   * e.g. "20% OFF — LEAFLY" or "$5.00 OFF — SAVE5"
   */
  function getDiscountBadgeText(discount) {
    var amountText = '';
    if (discount.percentage > 0) {
      amountText = discount.percentage + '% OFF';
    } else if (discount.fixed_amount > 0) {
      amountText = formatMoney(discount.fixed_amount, theme.moneyFormat) + ' OFF';
    }
    return amountText + ' &mdash; ' + discount.code.toUpperCase();
  }

  /**
   * Check whether this discount will actually reduce a given price.
   */
  function hasDiscount(discount) {
    return (discount.percentage > 0 || discount.fixed_amount > 0);
  }

  // =============================================
  // PRODUCT PAGE DISCOUNT
  // =============================================

  function updateProductPagePrice(discount) {
    if (!discount || !hasDiscount(discount)) return;

    var productSection = getProductSection();
    if (!productSection) return;

    var priceEl = productSection.querySelector('[data-product-price]');
    if (!priceEl) return;

    var originalPrice = null;
    try {
      if (typeof Alpine !== 'undefined') {
        var alpineData = Alpine.$data(productSection);
        if (alpineData && alpineData.current_variant) {
          originalPrice = alpineData.current_variant.price;
        }
      }
    } catch (e) {
      console.warn('[DiscountDisplay] Could not read Alpine data:', e);
    }

    if (!originalPrice) {
      try {
        var priceText = priceEl.textContent.replace(/[^0-9.]/g, '');
        originalPrice = Math.round(parseFloat(priceText) * 100);
      } catch (e) { /* ignore */ }
    }

    if (!originalPrice || originalPrice <= 0) return;

    var discountedPrice = getDiscountedPrice(originalPrice, discount);
    if (discountedPrice >= originalPrice) return;

    priceEl.setAttribute('data-original-price', 'true');
    priceEl.style.textDecoration = 'line-through';
    priceEl.style.opacity = '0.55';
    priceEl.style.display = 'inline';

    var container = productSection.querySelector('#discount-price-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'discount-price-container';
      container.style.cssText = 'margin-top: 4px;';
      priceEl.parentNode.appendChild(container);
    }

    container.innerHTML =
      '<span style="display: inline-flex; align-items: center; gap: 10px;">' +
        '<span style="font-size: 1.25em; font-weight: 700; color: #16a34a;">' +
          formatMoney(discountedPrice, theme.moneyFormat) +
        '</span>' +
        '<span style="padding: 4px 12px; ' +
          'background: #16a34a; color: #fff; ' +
          'font-size: 0.8em; border-radius: 20px; font-weight: 700; ' +
          'letter-spacing: 0.03em;">' +
          getDiscountBadgeText(discount) +
        '</span>' +
      '</span>' +
      '<div style="margin-top: 4px; font-size: 0.75em; color: #6b7280;">' +
        'FREE Shipping or $5 Priority Shipping' +
      '</div>';

    // Update the "Add to Cart – $XX.XX" button price.
    // current_price is a getter (computed from current_variant.price) so we
    // can't override it via assignment. Instead we find the button's price
    // span and replace its innerHTML directly, then disable Alpine's
    // x-html binding so it doesn't overwrite our value.
    try {
      var addToCartBtn = productSection.querySelector('.add-to-cart-btn');
      if (addToCartBtn) {
        // The price span is the one with x-html="formatMoney(current_price)"
        var btnPriceSpans = addToCartBtn.querySelectorAll('span[x-html]');
        btnPriceSpans.forEach(function (span) {
          var binding = span.getAttribute('x-html');
          if (binding && binding.indexOf('current_price') !== -1) {
            span.innerHTML = formatMoney(discountedPrice, theme.moneyFormat);
            // Remove x-html so Alpine doesn't overwrite on next tick
            span.removeAttribute('x-html');
            // Store the original binding so we can restore on variant change
            span.setAttribute('data-original-x-html', binding);
            console.log('[DiscountDisplay] Add to Cart button price updated to ' + formatMoney(discountedPrice, theme.moneyFormat));
          }
        });
      }
    } catch (e) {
      console.warn('[DiscountDisplay] Could not update Add to Cart price:', e);
    }

    console.log('[DiscountDisplay] Product price updated: ' +
      formatMoney(originalPrice, theme.moneyFormat) + ' → ' +
      formatMoney(discountedPrice, theme.moneyFormat) +
      ' (with ' + discount.code + ')');
  }

  function removeProductPageDiscount() {
    var productSection = getProductSection();
    if (!productSection) return;

    var priceEl = productSection.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.style.textDecoration = '';
      priceEl.style.opacity = '';
      priceEl.removeAttribute('data-original-price');
    }

    var container = productSection.querySelector('#discount-price-container');
    if (container) container.remove();

    // Restore the Add to Cart button's Alpine x-html binding so it
    // picks up the new variant's price before we re-apply the discount
    try {
      var addToCartBtn = productSection.querySelector('.add-to-cart-btn');
      if (addToCartBtn) {
        var restoredSpans = addToCartBtn.querySelectorAll('span[data-original-x-html]');
        restoredSpans.forEach(function (span) {
          var originalBinding = span.getAttribute('data-original-x-html');
          span.setAttribute('x-html', originalBinding);
          span.removeAttribute('data-original-x-html');
        });
      }
    } catch (e) { /* ignore */ }
  }

  // =============================================
  // COLLECTION PAGE DISCOUNT
  // =============================================

  function parsePriceCents(el) {
    if (!el) return 0;
    var text = el.textContent.trim();
    var cleaned = text.replace(/[^0-9.]/g, '');
    if (!cleaned) return 0;
    return Math.round(parseFloat(cleaned) * 100);
  }

  function updateCollectionPrices(discount) {
    if (!discount || !hasDiscount(discount)) return;

    var priceDivs = document.querySelectorAll('.product-tile .price');
    var count = 0;

    priceDivs.forEach(function (priceDiv) {
      if (priceDiv.getAttribute('data-discount-applied') === 'true') return;

      var regularContainer = priceDiv.querySelector('.price__regular:not(.hidden)');
      var saleContainer = priceDiv.querySelector('.price__sale:not(.hidden)');

      var activePriceEl = null;
      var originalCents = 0;

      if (saleContainer) {
        activePriceEl = saleContainer.querySelector('.price-item--sale');
        originalCents = parsePriceCents(activePriceEl);
      } else if (regularContainer) {
        activePriceEl = regularContainer.querySelector('.price-item--regular');
        originalCents = parsePriceCents(activePriceEl);
      }

      if (!activePriceEl || originalCents <= 0) return;

      var discountedCents = getDiscountedPrice(originalCents, discount);
      if (discountedCents >= originalCents) return;

      priceDiv.setAttribute('data-discount-applied', 'true');

      activePriceEl.style.textDecoration = 'line-through';
      activePriceEl.style.opacity = '0.55';
      activePriceEl.style.display = 'inline';

      var discountTag = document.createElement('span');
      discountTag.className = 'discount-collection-price';
      discountTag.innerHTML =
        '<span style="font-weight: 700; color: #16a34a; margin-left: 6px;">' +
          formatMoney(discountedCents, theme.moneyFormat) +
        '</span>';
      activePriceEl.parentNode.insertBefore(discountTag, activePriceEl.nextSibling);

      count++;
    });

    var qbPriceEls = document.querySelectorAll('.product-tile [data-product-price]');
    qbPriceEls.forEach(function (priceEl) {
      if (priceEl.closest('[data-product-section]')) return;
      if (priceEl.getAttribute('data-discount-applied') === 'true') return;

      var originalCents = parsePriceCents(priceEl);
      if (originalCents <= 0) return;

      var discountedCents = getDiscountedPrice(originalCents, discount);
      if (discountedCents >= originalCents) return;

      priceEl.setAttribute('data-discount-applied', 'true');
      priceEl.style.textDecoration = 'line-through';
      priceEl.style.opacity = '0.55';
      priceEl.style.display = 'inline';

      var discountTag = document.createElement('span');
      discountTag.className = 'discount-collection-price';
      discountTag.innerHTML =
        '<span style="font-weight: 700; color: #16a34a; margin-left: 6px;">' +
          formatMoney(discountedCents, theme.moneyFormat) +
        '</span>';
      priceEl.parentNode.insertBefore(discountTag, priceEl.nextSibling);

      count++;
    });

    if (count > 0) {
      console.log('[DiscountDisplay] Updated ' + count + ' collection tile price(s).');
    }
  }

  // =============================================
  // FLOATING DISCOUNT BANNER
  // =============================================

  function showFloatingBanner(discount) {
    if (!discount) return;
    if (document.getElementById('discount-floating-banner')) return;

    // Use custom label from config, or build default
    var titleText;
    if (discount.label) {
      titleText = discount.label;
    } else if (discount.percentage > 0) {
      titleText = discount.percentage + '% Discount Active!';
    } else if (discount.fixed_amount > 0) {
      titleText = formatMoney(discount.fixed_amount, theme.moneyFormat) + ' Off — Discount Active!';
    } else {
      titleText = 'Discount Active!';
    }

    var style = document.createElement('style');
    style.id = 'discount-floating-banner-styles';
    style.textContent =
      '#discount-floating-banner {' +
        'position: fixed;' +
        'bottom: 20px;' +
        'left: 20px;' +
        'z-index: 9999;' +
        'max-width: 320px;' +
        'font-family: inherit;' +
        'transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease;' +
      '}' +
      '#discount-floating-banner.dfb-hidden {' +
        'transform: translateX(-110%);' +
        'opacity: 0;' +
        'pointer-events: none;' +
      '}' +
      '#discount-floating-banner .dfb-card {' +
        'background: #111827;' +
        'color: #fff;' +
        'border-radius: 12px;' +
        'padding: 14px 18px;' +
        'box-shadow: 0 8px 30px rgba(0,0,0,0.25);' +
        'display: flex;' +
        'align-items: flex-start;' +
        'gap: 12px;' +
        'position: relative;' +
      '}' +
      '#discount-floating-banner .dfb-icon {' +
        'flex-shrink: 0;' +
        'width: 36px;' +
        'height: 36px;' +
        'background: #16a34a;' +
        'border-radius: 50%;' +
        'display: flex;' +
        'align-items: center;' +
        'justify-content: center;' +
        'font-size: 18px;' +
        'margin-top: 2px;' +
      '}' +
      '#discount-floating-banner .dfb-body {' +
        'flex: 1;' +
        'min-width: 0;' +
      '}' +
      '#discount-floating-banner .dfb-title {' +
        'font-weight: 700;' +
        'font-size: 15px;' +
        'margin: 0 0 3px 0;' +
        'line-height: 1.3;' +
      '}' +
      '#discount-floating-banner .dfb-subtitle {' +
        'font-size: 13px;' +
        'color: #d1d5db;' +
        'margin: 0;' +
        'line-height: 1.4;' +
      '}' +
      '#discount-floating-banner .dfb-code {' +
        'display: inline-block;' +
        'background: #16a34a;' +
        'color: #fff;' +
        'padding: 2px 8px;' +
        'border-radius: 4px;' +
        'font-weight: 700;' +
        'font-size: 12px;' +
        'letter-spacing: 0.05em;' +
        'margin-top: 6px;' +
      '}' +
      '#discount-floating-banner .dfb-close {' +
        'position: absolute;' +
        'top: 6px;' +
        'right: 8px;' +
        'background: none;' +
        'border: none;' +
        'color: #9ca3af;' +
        'cursor: pointer;' +
        'font-size: 18px;' +
        'line-height: 1;' +
        'padding: 2px 4px;' +
      '}' +
      '#discount-floating-banner .dfb-close:hover {' +
        'color: #fff;' +
      '}' +
      '#discount-floating-banner .dfb-minimized {' +
        'display: none;' +
        'background: #16a34a;' +
        'color: #fff;' +
        'border: none;' +
        'border-radius: 50%;' +
        'width: 48px;' +
        'height: 48px;' +
        'font-size: 22px;' +
        'cursor: pointer;' +
        'box-shadow: 0 4px 15px rgba(0,0,0,0.2);' +
        'align-items: center;' +
        'justify-content: center;' +
        'transition: transform 0.2s ease;' +
      '}' +
      '#discount-floating-banner .dfb-minimized:hover {' +
        'transform: scale(1.1);' +
      '}' +
      '@media (max-width: 480px) {' +
        '#discount-floating-banner {' +
          'left: 10px;' +
          'right: 10px;' +
          'bottom: 10px;' +
          'max-width: none;' +
        '}' +
      '}';
    document.head.appendChild(style);

    var banner = document.createElement('div');
    banner.id = 'discount-floating-banner';
    banner.className = 'dfb-hidden';
    banner.innerHTML =
      '<div class="dfb-card">' +
        '<button class="dfb-close" aria-label="Close">&times;</button>' +
        '<div class="dfb-icon">%</div>' +
        '<div class="dfb-body">' +
          '<p class="dfb-title">' + titleText + '</p>' +
          '<p class="dfb-subtitle">Your special discount will be applied at checkout automatically.</p>' +
          '<span class="dfb-code">Code: ' + discount.code.toUpperCase() + '</span>' +
        '</div>' +
      '</div>' +
      '<button class="dfb-minimized" aria-label="Show discount info">%</button>';

    document.body.appendChild(banner);

    var card = banner.querySelector('.dfb-card');
    var closeBtn = banner.querySelector('.dfb-close');
    var minimizedBtn = banner.querySelector('.dfb-minimized');

    setTimeout(function () {
      banner.classList.remove('dfb-hidden');
    }, 600);

    closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      card.style.display = 'none';
      minimizedBtn.style.display = 'flex';
    });

    minimizedBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      minimizedBtn.style.display = 'none';
      card.style.display = 'flex';
    });

    console.log('[DiscountDisplay] Floating banner shown for ' + discount.code + '.');
  }

  // =============================================
  // INITIALIZATION
  // =============================================

  async function init() {
    console.log('[DiscountDisplay] Initializing...');

    var discount = await getActiveDiscount();
    if (!discount) {
      console.log('[DiscountDisplay] No active discount code found.');
      return;
    }

    console.log('[DiscountDisplay] Active discount:', discount.code, '=', discount.percentage + '%');

    // --- Floating banner on ALL pages ---
    showFloatingBanner(discount);

    // --- Product page ---
    var productSection = getProductSection();
    if (productSection) {
      updateProductPagePrice(discount);

      productSection.addEventListener('shapes:product:variantchange', function (e) {
        setTimeout(function () {
          removeProductPageDiscount();
          if (e.detail && e.detail.variant && e.detail.variant.available) {
            updateProductPagePrice(discount);
          }
        }, 50);
      });
    }

    // --- Collection pages ---
    updateCollectionPrices(discount);
  }

  // Wait for DOM and Alpine to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(init, 800);
    });
  } else {
    setTimeout(init, 800);
  }
})();
